import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ContentDSessionListItem, ContentFoolscapSummary } from '@/lib/api'
import {
  listContentDSessions,
  createContentDSession,
  getContentDSessionWithTurns,
  deleteContentDSession as apiDeleteContentDSession,
  updateContentDSession,
  listAvailableContentFoolscaps,
} from '@/lib/api'

console.log('[content-d-store] Module loading...')

// =============================================================================
// CRITICAL: User isolation check - runs SYNCHRONOUSLY before store hydration
// =============================================================================

const CONTENT_D_USER_ID_KEY = 'content-d-store-user-id'

function clearStoreIfUserMismatch(): void {
  try {
    const storedUserId = localStorage.getItem(CONTENT_D_USER_ID_KEY)
    const hasContentDStore = localStorage.getItem('content-d-store') !== null

    console.log('[content-d-store] SYNC: Checking user mismatch')
    console.log('[content-d-store] SYNC: Stored user ID:', storedUserId)
    console.log('[content-d-store] SYNC: Has content-d-store data:', hasContentDStore)

    // Find Supabase auth token in localStorage
    let currentUserId: string | null = null

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
        try {
          const rawValue = localStorage.getItem(key)
          const authData = JSON.parse(rawValue || '{}')
          currentUserId = authData?.user?.id || authData?.session?.user?.id || null
          if (currentUserId) break
        } catch (e) {
          // Invalid JSON, skip
        }
      }
    }

    // CASE 1: We have a stored user ID and it doesn't match current user
    if (storedUserId && currentUserId && storedUserId !== currentUserId) {
      console.log('[content-d-store] SYNC: User mismatch detected! Clearing stale data')
      localStorage.removeItem('content-d-store')
      localStorage.setItem(CONTENT_D_USER_ID_KEY, currentUserId)
    }
    // CASE 2: No stored user ID but we have content-d-store data AND a current user
    else if (!storedUserId && hasContentDStore && currentUserId) {
      console.log('[content-d-store] SYNC: No stored user ID but content-d-store exists. Clearing orphaned data.')
      localStorage.removeItem('content-d-store')
      localStorage.setItem(CONTENT_D_USER_ID_KEY, currentUserId)
    }
    // CASE 3: We have a current user but no stored user ID (fresh start)
    else if (!storedUserId && currentUserId) {
      console.log('[content-d-store] SYNC: New user, storing user ID')
      localStorage.setItem(CONTENT_D_USER_ID_KEY, currentUserId)
    }
    // CASE 4: No current user (not logged in)
    else if (!currentUserId) {
      console.log('[content-d-store] SYNC: No Supabase auth token found in localStorage')
      if (hasContentDStore) {
        console.log('[content-d-store] SYNC: Clearing orphaned content-d-store (no logged in user)')
        localStorage.removeItem('content-d-store')
        localStorage.removeItem(CONTENT_D_USER_ID_KEY)
      }
    }
  } catch (e) {
    console.error('[content-d-store] Error checking user mismatch:', e)
  }
}

// Run the check immediately when this module is loaded
clearStoreIfUserMismatch()

// =============================================================================
// Types
// =============================================================================

export interface FinalDiffData {
  has_changes: boolean
  original_doc: string
  final_doc: string
  stats: {
    blocks_added: number
    blocks_deleted: number
    blocks_modified: number
    blocks_unchanged: number
  }
  changes: Array<{
    type: 'added' | 'deleted' | 'modified'
    content?: string
    modifications?: Array<{
      deleted: string
      added: string
      context: string
    }>
  }>
}

export interface Turn {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  thinking?: string  // Extended thinking content
  zinsserThinking?: string
  strengthenVerbsThinking?: string
  checkLogicThinking?: string
  rhythmSoundThinking?: string
  responderThinking?: string
  // Node execution flags
  zinsserRan?: boolean
  strengthenVerbsRan?: boolean
  checkLogicRan?: boolean
  rhythmSoundRan?: boolean
  responderRan?: boolean
  // Final diff for this turn
  finalDiff?: FinalDiffData | null
  showFinalDiff?: boolean
  // Hidden flag - turn exists but not shown in UI (e.g., auto-generated initial turn)
  isHidden?: boolean
}

export interface VoiceDirection {
  hook_type?: 'deadpan_inversion' | 'permission_statement' | 'systemic_accusation'
  primary_mode?: 'deadpan' | 'earnest'
  framework_type?: 'numbered_set' | 'named_model' | 'dichotomy' | 'spectrum'
  kicker_style?: 'absurdist' | 'philosophical' | 'call_to_becoming' | 'dropped_mic'
  target_reader?: string
}

export interface GatekeeperStreamingContent {
  response: string
  session_id?: string
  thinking?: string
  zinsser?: boolean  // Whether workflow continues to Zinsser (affects status handling)
}

type WorkflowStatus = 'idle' | 'submitting' | 'processing' | 'streaming' | 'complete' | 'error'
export type ContentDMode = 'gatekeeper' | 'draft'

// =============================================================================
// Store Interface
// =============================================================================

interface ContentDState {
  // Session
  sessionId: string | null
  sessionName: string

  // Session list (for picker)
  sessions: ContentDSessionListItem[]
  isLoadingSessions: boolean
  showSessionPicker: boolean

  // ContentFoolscap selection
  availableContentFoolscaps: ContentFoolscapSummary[]
  availableFoolscaps: ContentFoolscapSummary[]  // Alias
  isLoadingContentFoolscaps: boolean
  isLoadingFoolscaps: boolean  // Alias
  showContentFoolscapPicker: boolean
  showFoolscapPicker: boolean  // Alias
  selectedContentFoolscapId: string | null
  contentFoolscapSnapshot: string  // Read-only reference content
  foolscapSnapshot: string  // Alias for contentFoolscapSnapshot

  // Session sidebar (left panel)
  showSessionSidebar: boolean

  // Mobile view state
  mobileView: 'sessions' | 'chat' | 'document'

  // ContentD mode (gatekeeper | draft)
  contentDMode: ContentDMode

  // Track if initial draft has run for current session (prevents re-run on refresh)
  hasInitialDraftRun: boolean

  // Flag set when session is created - ContentDChat checks this once to trigger initial draft
  pendingInitialTrigger: boolean

  // Workflow status
  workflowStatus: WorkflowStatus
  currentTaskId: string | null
  error: string | null

  // Document state
  documentContent: string  // Annotated content for display
  documentCleanContent: string  // Clean content for next turn diff
  documentDisplayContent: string | null  // Transient display content during streaming

  // Voice direction (gathered from Gatekeeper)
  voiceDirection: VoiceDirection | null

  // Gatekeeper state (voice direction gathering)
  gatekeeperTurns: Turn[]
  gatekeeperStreamingContent: GatekeeperStreamingContent | null
  gatekeeperStreamingThinking: string | null
  gatekeeperIsThinking: boolean
  gatekeeperIsProcessing: boolean
  gatekeeperHasThinking: boolean

  // Zinsser state (drafting with hybrid voice)
  zinsserDoc: string
  zinsserDiagnosis: {
    controlling_idea?: string
    audience_contract?: string
    structure_issues?: string[]
    evidence_gaps?: string[]
    opening_closing?: string
    voice_consistency?: string
  } | null
  zinsserChangesMade: string[]
  zinsserStreamingThinking: string | null
  zinsserIsThinking: boolean
  zinsserIsProcessing: boolean
  zinsserHasThinking: boolean

  // StrengthenVerbs state (micro editing - Pass 1)
  strengthenVerbsChangesMade: string[]
  strengthenVerbsStreamingThinking: string | null
  strengthenVerbsIsThinking: boolean
  strengthenVerbsIsProcessing: boolean
  strengthenVerbsHasThinking: boolean

  // CheckLogic state (micro editing - Pass 2)
  checkLogicChangesMade: string[]
  checkLogicStreamingThinking: string | null
  checkLogicIsThinking: boolean
  checkLogicIsProcessing: boolean
  checkLogicHasThinking: boolean

  // RhythmSound state (micro editing - Pass 3)
  rhythmSoundChangesMade: string[]
  rhythmSoundStreamingThinking: string | null
  rhythmSoundIsThinking: boolean
  rhythmSoundIsProcessing: boolean
  rhythmSoundHasThinking: boolean

  // Responder state (coaching response after drafting)
  responderResponse: string | null
  responderFollowUpQuestion: string | null
  responderStreamingThinking: string | null
  responderIsThinking: boolean
  responderIsProcessing: boolean
  responderHasThinking: boolean

  // Final diff state
  finalDiff: FinalDiffData | null
  showFinalDiff: boolean

  // Actions
  setContentDMode: (mode: ContentDMode) => void
  setHasInitialDraftRun: (hasRun: boolean) => void
  clearPendingInitialTrigger: () => void
  setSessionName: (name: string) => void
  setWorkflowStatus: (status: WorkflowStatus) => void
  setCurrentTaskId: (taskId: string | null) => void
  setError: (error: string | null) => void
  startNewSession: () => void

  // Session management actions
  openSessionPicker: () => void
  closeSessionPicker: () => void
  openSessionSidebar: () => void
  closeSessionSidebar: () => void
  toggleSessionSidebar: () => void
  setMobileView: (view: 'sessions' | 'chat' | 'document') => void
  fetchSessions: () => Promise<void>
  loadSessionFromDb: (sessionId: string) => Promise<void>
  deleteSession: (sessionId: string) => Promise<void>
  renameSession: (name: string) => Promise<void>

  // ContentFoolscap selection actions
  openContentFoolscapPicker: () => void
  openFoolscapPicker: () => void  // Alias for openContentFoolscapPicker
  closeContentFoolscapPicker: () => void
  closeFoolscapPicker: () => void  // Alias for closeContentFoolscapPicker
  fetchAvailableContentFoolscaps: () => Promise<void>
  fetchAvailableFoolscaps: () => Promise<void>  // Alias for fetchAvailableContentFoolscaps
  selectContentFoolscap: (contentFoolscapId: string) => void
  createSessionFromContentFoolscap: (contentFoolscapId: string, name?: string) => Promise<void>
  createSessionFromFoolscap: (contentFoolscapId: string, name?: string) => Promise<void>  // Alias

  // Document actions
  updateDocumentContent: (content: string) => void

  // Voice direction actions
  setVoiceDirection: (direction: VoiceDirection) => void

  // Gatekeeper actions
  addGatekeeperUserTurn: (content: string) => void
  startGatekeeperStreaming: () => void
  startGatekeeperThinking: () => void
  updateGatekeeperThinking: (delta: string) => void
  finishGatekeeperThinking: () => void
  finishGatekeeperStreaming: (output: GatekeeperStreamingContent) => void

  // Zinsser actions
  startZinsserProcessing: () => void
  startZinsserThinking: () => void
  updateZinsserThinking: (delta: string) => void
  finishZinsserThinking: () => void
  finishZinsserProcessing: (output: {
    zin_doc: string
    diagnosis: {
      controlling_idea?: string
      audience_contract?: string
      structure_issues?: string[]
      evidence_gaps?: string[]
      opening_closing?: string
      voice_consistency?: string
    }
    changes_made: string[]
  }) => void

  // StrengthenVerbs actions (micro editing - Pass 1)
  startStrengthenVerbsProcessing: () => void
  startStrengthenVerbsThinking: () => void
  updateStrengthenVerbsThinking: (delta: string) => void
  finishStrengthenVerbsThinking: () => void
  finishStrengthenVerbsProcessing: (output: {
    zin_doc: string
    changes_made: string[]
  }) => void

  // CheckLogic actions (micro editing - Pass 2)
  startCheckLogicProcessing: () => void
  startCheckLogicThinking: () => void
  updateCheckLogicThinking: (delta: string) => void
  finishCheckLogicThinking: () => void
  finishCheckLogicProcessing: (output: {
    zin_doc: string
    changes_made: string[]
  }) => void

  // RhythmSound actions (micro editing - Pass 3)
  startRhythmSoundProcessing: () => void
  startRhythmSoundThinking: () => void
  updateRhythmSoundThinking: (delta: string) => void
  finishRhythmSoundThinking: () => void
  finishRhythmSoundProcessing: (output: {
    zin_doc: string
    changes_made: string[]
  }) => void

  // Responder actions
  startResponderProcessing: () => void
  startResponderThinking: () => void
  updateResponderThinking: (delta: string) => void
  finishResponderThinking: () => void
  finishResponderProcessing: (output: {
    response: string
    follow_up_question?: string | null
  }) => void

  // Final diff actions
  setFinalDiff: (diff: FinalDiffData | null) => void
  showFinalDiffViewer: () => void
  hideFinalDiffViewer: () => void
  hideTurnDiffViewer: (turnIndex: number) => void

  // Real-time canvas editing
  applyCanvasEdit: (oldStr: string, newStr: string) => void
  applyCanvasInsert: (insertLine: number, insertText: string) => void
}

// =============================================================================
// Store Implementation
// =============================================================================

export const useContentDStore = create<ContentDState>()(
  persist(
    (set, get) => ({
      // Initial state
      sessionId: null,
      sessionName: 'New Draft',
      sessions: [],
      isLoadingSessions: false,
      showSessionPicker: false,
      availableContentFoolscaps: [],
      availableFoolscaps: [],  // Alias - kept in sync
      isLoadingContentFoolscaps: false,
      isLoadingFoolscaps: false,  // Alias - kept in sync
      showContentFoolscapPicker: false,
      showFoolscapPicker: false,  // Alias - kept in sync
      selectedContentFoolscapId: null,
      contentFoolscapSnapshot: '',
      foolscapSnapshot: '',  // Alias - kept in sync with contentFoolscapSnapshot
      showSessionSidebar: false,
      mobileView: 'chat',
      contentDMode: 'gatekeeper',
      hasInitialDraftRun: false,
      pendingInitialTrigger: false,
      workflowStatus: 'idle',
      currentTaskId: null,
      error: null,

      // Document state
      documentContent: '',
      documentCleanContent: '',
      documentDisplayContent: null,

      // Voice direction
      voiceDirection: null,

      // Gatekeeper state
      gatekeeperTurns: [],
      gatekeeperStreamingContent: null,
      gatekeeperStreamingThinking: null,
      gatekeeperIsThinking: false,
      gatekeeperIsProcessing: false,
      gatekeeperHasThinking: false,

      // Zinsser state
      zinsserDoc: '',
      zinsserDiagnosis: null,
      zinsserChangesMade: [],
      zinsserStreamingThinking: null,
      zinsserIsThinking: false,
      zinsserIsProcessing: false,
      zinsserHasThinking: false,

      // StrengthenVerbs state
      strengthenVerbsChangesMade: [],
      strengthenVerbsStreamingThinking: null,
      strengthenVerbsIsThinking: false,
      strengthenVerbsIsProcessing: false,
      strengthenVerbsHasThinking: false,

      // CheckLogic state
      checkLogicChangesMade: [],
      checkLogicStreamingThinking: null,
      checkLogicIsThinking: false,
      checkLogicIsProcessing: false,
      checkLogicHasThinking: false,

      // RhythmSound state
      rhythmSoundChangesMade: [],
      rhythmSoundStreamingThinking: null,
      rhythmSoundIsThinking: false,
      rhythmSoundIsProcessing: false,
      rhythmSoundHasThinking: false,

      // Responder state
      responderResponse: null,
      responderFollowUpQuestion: null,
      responderStreamingThinking: null,
      responderIsThinking: false,
      responderIsProcessing: false,
      responderHasThinking: false,

      // Final diff state
      finalDiff: null,
      showFinalDiff: false,

      // Basic actions
      setContentDMode: (mode) => set({ contentDMode: mode }),
      setHasInitialDraftRun: (hasRun) => set({ hasInitialDraftRun: hasRun }),
      clearPendingInitialTrigger: () => set({ pendingInitialTrigger: false }),
      setSessionName: (name) => set({ sessionName: name }),
      setWorkflowStatus: (status) => set({ workflowStatus: status }),
      setCurrentTaskId: (taskId) => set({ currentTaskId: taskId }),
      setError: (error) => set({ error }),

      startNewSession: () => set({
        sessionId: null,
        sessionName: 'New Draft',
        selectedContentFoolscapId: null,
        contentFoolscapSnapshot: '',
        foolscapSnapshot: '',  // Alias
        contentDMode: 'gatekeeper',
        pendingInitialTrigger: false,
        workflowStatus: 'idle',
        currentTaskId: null,
        error: null,
        documentContent: '',
        documentCleanContent: '',
        documentDisplayContent: null,
        voiceDirection: null,
        gatekeeperTurns: [],
        gatekeeperStreamingContent: null,
        gatekeeperStreamingThinking: null,
        gatekeeperIsThinking: false,
        gatekeeperIsProcessing: false,
        gatekeeperHasThinking: false,
        zinsserDoc: '',
        zinsserDiagnosis: null,
        zinsserChangesMade: [],
        zinsserStreamingThinking: null,
        zinsserIsThinking: false,
        zinsserIsProcessing: false,
        zinsserHasThinking: false,
        strengthenVerbsChangesMade: [],
        strengthenVerbsStreamingThinking: null,
        strengthenVerbsIsThinking: false,
        strengthenVerbsIsProcessing: false,
        strengthenVerbsHasThinking: false,
        checkLogicChangesMade: [],
        checkLogicStreamingThinking: null,
        checkLogicIsThinking: false,
        checkLogicIsProcessing: false,
        checkLogicHasThinking: false,
        rhythmSoundChangesMade: [],
        rhythmSoundStreamingThinking: null,
        rhythmSoundIsThinking: false,
        rhythmSoundIsProcessing: false,
        rhythmSoundHasThinking: false,
        responderResponse: null,
        responderFollowUpQuestion: null,
        responderStreamingThinking: null,
        responderIsThinking: false,
        responderIsProcessing: false,
        responderHasThinking: false,
        finalDiff: null,
        showFinalDiff: false,
        showContentFoolscapPicker: true,  // Show picker for new session
        showFoolscapPicker: true,  // Alias
      }),

      // Session management actions
      openSessionPicker: () => set({ showSessionPicker: true }),
      closeSessionPicker: () => set({ showSessionPicker: false }),
      openSessionSidebar: () => set({ showSessionSidebar: true }),
      closeSessionSidebar: () => set({ showSessionSidebar: false }),
      toggleSessionSidebar: () => set((state) => ({ showSessionSidebar: !state.showSessionSidebar })),
      setMobileView: (view) => set({ mobileView: view }),

      fetchSessions: async () => {
        set({ isLoadingSessions: true })
        try {
          const sessions = await listContentDSessions()
          set({ sessions, isLoadingSessions: false })
        } catch (error) {
          console.error('[content-d-store] Failed to fetch sessions:', error)
          set({ isLoadingSessions: false })
        }
      },

      loadSessionFromDb: async (sessionId: string) => {
        try {
          const session = await getContentDSessionWithTurns(sessionId)
          const turns = (session.turns || []).map((t: { role: string; content: string; timestamp: string; thinking?: string }) => ({
            role: t.role as 'user' | 'assistant',
            content: t.content,
            timestamp: t.timestamp,
            thinking: t.thinking,
          }))
          // Note: Backend saves final edited doc to old_doc (baseline for next diff)
          // So we load old_doc as the document content to display
          const docContent = session.old_doc || session.new_doc || ''
          const snapshotContent = session.foolscap_snapshot || session.content_foolscap_snapshot || ''
          set({
            sessionId: session.session_id,
            sessionName: session.name || 'Untitled Draft',
            selectedContentFoolscapId: session.source_content_f_id || null,
            contentFoolscapSnapshot: snapshotContent,
            foolscapSnapshot: snapshotContent,  // Alias
            documentContent: docContent,
            documentCleanContent: docContent,
            voiceDirection: session.voice_direction || null,
            contentDMode: session.session_context?.phase === 'drafting' ? 'draft' : 'gatekeeper',
            hasInitialDraftRun: turns.length > 0,  // If has turns, initial draft already ran
            gatekeeperTurns: turns,
            showSessionPicker: false,
            showContentFoolscapPicker: false,
            showFoolscapPicker: false,
          })
        } catch (error) {
          console.error('[content-d-store] Failed to load session:', error)
          set({ error: 'Failed to load session' })
        }
      },

      deleteSession: async (sessionId: string) => {
        try {
          await apiDeleteContentDSession(sessionId)
          const { sessions, sessionId: currentSessionId } = get()
          set({
            sessions: sessions.filter(s => s.session_id !== sessionId),
          })
          // If we deleted the current session, start a new one
          if (currentSessionId === sessionId) {
            get().startNewSession()
          }
        } catch (error) {
          console.error('[content-d-store] Failed to delete session:', error)
          set({ error: 'Failed to delete session' })
        }
      },

      renameSession: async (name: string) => {
        const { sessionId } = get()
        if (!sessionId) return
        try {
          await updateContentDSession(sessionId, { name })
          set({ sessionName: name })
          // Refresh sessions list
          get().fetchSessions()
        } catch (error) {
          console.error('[content-d-store] Failed to rename session:', error)
        }
      },

      // ContentFoolscap selection actions
      openContentFoolscapPicker: () => set({ showContentFoolscapPicker: true, showFoolscapPicker: true }),
      openFoolscapPicker: () => set({ showContentFoolscapPicker: true, showFoolscapPicker: true }),
      closeContentFoolscapPicker: () => set({ showContentFoolscapPicker: false, showFoolscapPicker: false }),
      closeFoolscapPicker: () => set({ showContentFoolscapPicker: false, showFoolscapPicker: false }),

      fetchAvailableContentFoolscaps: async () => {
        console.log('[content-d-store] Fetching available content foolscaps...')
        set({ isLoadingContentFoolscaps: true, isLoadingFoolscaps: true })
        try {
          const contentFoolscaps = await listAvailableContentFoolscaps()
          console.log('[content-d-store] Fetched content foolscaps:', contentFoolscaps.length, contentFoolscaps)
          set({
            availableContentFoolscaps: contentFoolscaps,
            availableFoolscaps: contentFoolscaps,
            isLoadingContentFoolscaps: false,
            isLoadingFoolscaps: false,
          })
        } catch (error) {
          console.error('[content-d-store] Failed to fetch content foolscaps:', error)
          set({
            availableContentFoolscaps: [],
            availableFoolscaps: [],
            isLoadingContentFoolscaps: false,
            isLoadingFoolscaps: false,
          })
        }
      },

      selectContentFoolscap: (contentFoolscapId: string) => {
        const { availableContentFoolscaps } = get()
        const contentFoolscap = availableContentFoolscaps.find(f => f.session_id === contentFoolscapId)
        if (contentFoolscap) {
          const snapshotContent = contentFoolscap.foolscap_markdown || ''
          set({
            selectedContentFoolscapId: contentFoolscapId,
            contentFoolscapSnapshot: snapshotContent,
            foolscapSnapshot: snapshotContent,  // Alias
          })
        }
      },

      // Alias for fetchAvailableContentFoolscaps
      fetchAvailableFoolscaps: async () => {
        return get().fetchAvailableContentFoolscaps()
      },

      createSessionFromContentFoolscap: async (contentFoolscapId: string, name?: string) => {
        try {
          const { availableContentFoolscaps } = get()
          const contentFoolscap = availableContentFoolscaps.find(f => f.session_id === contentFoolscapId)

          const session = await createContentDSession(contentFoolscapId, name)

          const snapshotContent = session.foolscap_snapshot || session.content_foolscap_snapshot || contentFoolscap?.foolscap_markdown || ''
          set({
            sessionId: session.session_id,
            sessionName: session.name || contentFoolscap?.name || 'New Draft',
            selectedContentFoolscapId: contentFoolscapId,
            contentFoolscapSnapshot: snapshotContent,
            foolscapSnapshot: snapshotContent,  // Alias
            documentContent: '',
            documentCleanContent: '',
            documentDisplayContent: null,
            voiceDirection: null,
            contentDMode: 'gatekeeper',  // Use gatekeeper mode for multi-turn conversation
            hasInitialDraftRun: false,  // Will be set true after trigger
            pendingInitialTrigger: true,  // Auto-trigger Gatekeeper to lead the interview
            workflowStatus: 'idle',  // Reset workflow status for new session
            currentTaskId: null,
            error: null,
            gatekeeperTurns: [],  // Start empty - Gatekeeper initiates conversation
            // Reset all Gatekeeper state
            gatekeeperStreamingContent: null,
            gatekeeperStreamingThinking: null,
            gatekeeperIsThinking: false,
            gatekeeperIsProcessing: false,
            gatekeeperHasThinking: false,
            // Reset all Zinsser state
            zinsserDoc: '',
            zinsserDiagnosis: null,
            zinsserChangesMade: [],
            zinsserStreamingThinking: null,
            zinsserIsThinking: false,
            zinsserIsProcessing: false,
            zinsserHasThinking: false,
            // Reset all StrengthenVerbs state
            strengthenVerbsChangesMade: [],
            strengthenVerbsStreamingThinking: null,
            strengthenVerbsIsThinking: false,
            strengthenVerbsIsProcessing: false,
            strengthenVerbsHasThinking: false,
            // Reset all CheckLogic state
            checkLogicChangesMade: [],
            checkLogicStreamingThinking: null,
            checkLogicIsThinking: false,
            checkLogicIsProcessing: false,
            checkLogicHasThinking: false,
            // Reset all RhythmSound state
            rhythmSoundChangesMade: [],
            rhythmSoundStreamingThinking: null,
            rhythmSoundIsThinking: false,
            rhythmSoundIsProcessing: false,
            rhythmSoundHasThinking: false,
            // Reset all Responder state
            responderResponse: null,
            responderFollowUpQuestion: null,
            responderStreamingThinking: null,
            responderIsThinking: false,
            responderIsProcessing: false,
            responderHasThinking: false,
            // Reset final diff state
            finalDiff: null,
            showFinalDiff: false,
            // Close pickers
            showContentFoolscapPicker: false,
            showFoolscapPicker: false,  // Keep alias in sync
            showSessionPicker: false,
          })
          // Refresh sessions list
          get().fetchSessions()
        } catch (error) {
          console.error('[content-d-store] Failed to create session from content foolscap:', error)
          set({ error: 'Failed to create draft session' })
        }
      },

      // Alias for createSessionFromContentFoolscap
      createSessionFromFoolscap: async (contentFoolscapId: string, name?: string) => {
        return get().createSessionFromContentFoolscap(contentFoolscapId, name)
      },

      // Document actions
      updateDocumentContent: (content: string) => set({ documentContent: content }),

      // Voice direction actions
      setVoiceDirection: (direction: VoiceDirection) => set({ voiceDirection: direction }),

      // Gatekeeper actions
      addGatekeeperUserTurn: (content: string) => set((state) => ({
        gatekeeperTurns: [
          ...state.gatekeeperTurns,
          {
            role: 'user' as const,
            content,
            timestamp: new Date().toISOString(),
          },
        ],
      })),

      startGatekeeperStreaming: () => set({
        workflowStatus: 'streaming',
        gatekeeperStreamingContent: { response: '' },
        gatekeeperStreamingThinking: null,
        gatekeeperIsThinking: false,
        gatekeeperIsProcessing: true,  // Processing starts - keeps status visible
      }),

      startGatekeeperThinking: () => set({
        workflowStatus: 'processing',  // Required for UI to show status indicators
        gatekeeperIsThinking: true,
        gatekeeperIsProcessing: true,  // Processing active during thinking
        gatekeeperStreamingThinking: '',
        gatekeeperHasThinking: true,
      }),

      updateGatekeeperThinking: (delta: string) => set((state) => ({
        gatekeeperStreamingThinking: (state.gatekeeperStreamingThinking || '') + delta,
      })),

      finishGatekeeperThinking: () => set({
        gatekeeperIsThinking: false,
        // gatekeeperIsProcessing stays true - we're still generating response
      }),

      finishGatekeeperStreaming: (output: GatekeeperStreamingContent) => set((state) => ({
        // Only mark complete if NOT continuing to Zinsser
        // When zinsser=true, the workflow continues and Responder will set complete
        workflowStatus: output.zinsser ? 'processing' : 'complete',
        gatekeeperTurns: [
          ...state.gatekeeperTurns,
          {
            role: 'assistant' as const,
            content: output.response,
            timestamp: new Date().toISOString(),
            thinking: state.gatekeeperStreamingThinking || undefined,
          },
        ],
        gatekeeperStreamingContent: null,
        gatekeeperStreamingThinking: null,
        gatekeeperIsProcessing: false,  // Gatekeeper processing complete
      })),

      // Zinsser actions
      startZinsserProcessing: () => set({
        workflowStatus: 'processing',  // Keep workflow active so UI shows indicators
        zinsserIsProcessing: true,
        zinsserStreamingThinking: null,
        zinsserIsThinking: false,
        zinsserHasThinking: false,
      }),

      startZinsserThinking: () => set({
        zinsserIsThinking: true,
        zinsserStreamingThinking: '',
        zinsserHasThinking: true,
      }),

      updateZinsserThinking: (delta: string) => set((state) => ({
        zinsserStreamingThinking: (state.zinsserStreamingThinking || '') + delta,
      })),

      finishZinsserThinking: () => set({ zinsserIsThinking: false }),

      finishZinsserProcessing: (output) => set((state) => {
        // Update the last assistant turn with Zinsser thinking
        const turns = [...state.gatekeeperTurns]
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            zinsserThinking: state.zinsserStreamingThinking || undefined,
            zinsserRan: true,
          }
        }
        return {
          zinsserIsProcessing: false,
          zinsserDoc: output.zin_doc,
          zinsserDiagnosis: output.diagnosis,
          zinsserChangesMade: output.changes_made,
          documentContent: output.zin_doc,  // Update document with Zinsser output
          gatekeeperTurns: turns,
        }
      }),

      // StrengthenVerbs actions
      startStrengthenVerbsProcessing: () => set({
        workflowStatus: 'processing',  // Keep workflow active so UI shows indicators
        strengthenVerbsIsProcessing: true,
        strengthenVerbsStreamingThinking: null,
        strengthenVerbsIsThinking: false,
        strengthenVerbsHasThinking: false,
      }),

      startStrengthenVerbsThinking: () => set({
        strengthenVerbsIsThinking: true,
        strengthenVerbsStreamingThinking: '',
        strengthenVerbsHasThinking: true,
      }),

      updateStrengthenVerbsThinking: (delta: string) => set((state) => ({
        strengthenVerbsStreamingThinking: (state.strengthenVerbsStreamingThinking || '') + delta,
      })),

      finishStrengthenVerbsThinking: () => set({ strengthenVerbsIsThinking: false }),

      finishStrengthenVerbsProcessing: (output) => set((state) => {
        const turns = [...state.gatekeeperTurns]
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            strengthenVerbsThinking: state.strengthenVerbsStreamingThinking || undefined,
            strengthenVerbsRan: true,
          }
        }
        return {
          strengthenVerbsIsProcessing: false,
          strengthenVerbsChangesMade: output.changes_made,
          documentContent: output.zin_doc,
          gatekeeperTurns: turns,
        }
      }),

      // CheckLogic actions
      startCheckLogicProcessing: () => set({
        workflowStatus: 'processing',  // Keep workflow active so UI shows indicators
        checkLogicIsProcessing: true,
        checkLogicStreamingThinking: null,
        checkLogicIsThinking: false,
        checkLogicHasThinking: false,
      }),

      startCheckLogicThinking: () => set({
        checkLogicIsThinking: true,
        checkLogicStreamingThinking: '',
        checkLogicHasThinking: true,
      }),

      updateCheckLogicThinking: (delta: string) => set((state) => ({
        checkLogicStreamingThinking: (state.checkLogicStreamingThinking || '') + delta,
      })),

      finishCheckLogicThinking: () => set({ checkLogicIsThinking: false }),

      finishCheckLogicProcessing: (output) => set((state) => {
        const turns = [...state.gatekeeperTurns]
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            checkLogicThinking: state.checkLogicStreamingThinking || undefined,
            checkLogicRan: true,
          }
        }
        return {
          checkLogicIsProcessing: false,
          checkLogicChangesMade: output.changes_made,
          documentContent: output.zin_doc,
          gatekeeperTurns: turns,
        }
      }),

      // RhythmSound actions
      startRhythmSoundProcessing: () => set({
        workflowStatus: 'processing',  // Keep workflow active so UI shows indicators
        rhythmSoundIsProcessing: true,
        rhythmSoundStreamingThinking: null,
        rhythmSoundIsThinking: false,
        rhythmSoundHasThinking: false,
      }),

      startRhythmSoundThinking: () => set({
        rhythmSoundIsThinking: true,
        rhythmSoundStreamingThinking: '',
        rhythmSoundHasThinking: true,
      }),

      updateRhythmSoundThinking: (delta: string) => set((state) => ({
        rhythmSoundStreamingThinking: (state.rhythmSoundStreamingThinking || '') + delta,
      })),

      finishRhythmSoundThinking: () => set({ rhythmSoundIsThinking: false }),

      finishRhythmSoundProcessing: (output) => set((state) => {
        const turns = [...state.gatekeeperTurns]
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            rhythmSoundThinking: state.rhythmSoundStreamingThinking || undefined,
            rhythmSoundRan: true,
          }
        }
        return {
          rhythmSoundIsProcessing: false,
          rhythmSoundChangesMade: output.changes_made,
          documentContent: output.zin_doc,
          gatekeeperTurns: turns,
        }
      }),

      // Responder actions
      startResponderProcessing: () => set({
        workflowStatus: 'processing',  // Keep workflow active so UI shows indicators
        responderIsProcessing: true,
        responderStreamingThinking: null,
        responderIsThinking: false,
        responderHasThinking: false,
      }),

      startResponderThinking: () => set({
        responderIsThinking: true,
        responderStreamingThinking: '',
        responderHasThinking: true,
      }),

      updateResponderThinking: (delta: string) => set((state) => ({
        responderStreamingThinking: (state.responderStreamingThinking || '') + delta,
      })),

      finishResponderThinking: () => set({ responderIsThinking: false }),

      finishResponderProcessing: (output) => set((state) => {
        const turns = [...state.gatekeeperTurns]
        const responderContent = output.response || ''

        // Check if we have an assistant turn to append to
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          // Append the responder response to the existing assistant turn
          const existingContent = turns[turns.length - 1].content || ''

          // Only append if there's a responder response
          const newContent = responderContent
            ? (existingContent ? `${existingContent}\n\n---\n\n${responderContent}` : responderContent)
            : existingContent

          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            content: newContent,
            responderThinking: state.responderStreamingThinking || undefined,
            responderRan: true,
          }
        } else if (responderContent) {
          // No assistant turn exists yet (ContentD workflow skips Gatekeeper)
          // Create a new assistant turn with the responder response
          turns.push({
            role: 'assistant' as const,
            content: responderContent,
            timestamp: new Date().toISOString(),
            responderThinking: state.responderStreamingThinking || undefined,
            responderRan: true,
            // Mark thinking from editing passes
            zinsserThinking: state.zinsserStreamingThinking || undefined,
            zinsserRan: state.zinsserHasThinking,
            strengthenVerbsThinking: state.strengthenVerbsStreamingThinking || undefined,
            strengthenVerbsRan: state.strengthenVerbsHasThinking,
            checkLogicThinking: state.checkLogicStreamingThinking || undefined,
            checkLogicRan: state.checkLogicHasThinking,
            rhythmSoundThinking: state.rhythmSoundStreamingThinking || undefined,
            rhythmSoundRan: state.rhythmSoundHasThinking,
          })
        }

        return {
          responderIsProcessing: false,
          responderResponse: output.response,
          responderFollowUpQuestion: output.follow_up_question || null,
          responderHasThinking: true,
          workflowStatus: 'complete',  // Mark workflow as complete when responder finishes
          gatekeeperTurns: turns,
        }
      }),

      // Final diff actions
      setFinalDiff: (diff) => set((state) => {
        // Store globally and attach to the last turn
        const turns = [...state.gatekeeperTurns]
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            finalDiff: diff,
            showFinalDiff: diff?.has_changes || false,
          }
        }
        return {
          finalDiff: diff,
          gatekeeperTurns: turns,
        }
      }),
      showFinalDiffViewer: () => set({ showFinalDiff: true }),
      hideFinalDiffViewer: () => set({ showFinalDiff: false }),
      hideTurnDiffViewer: (turnIndex: number) => set((state) => {
        const turns = [...state.gatekeeperTurns]
        if (turns[turnIndex]) {
          turns[turnIndex] = { ...turns[turnIndex], showFinalDiff: false }
        }
        return { gatekeeperTurns: turns }
      }),

      // Real-time canvas editing
      applyCanvasEdit: (oldStr: string, newStr: string) => set((state) => {
        const content = state.documentContent || ''
        const found = content.includes(oldStr)
        console.log('[applyCanvasEdit] Found match:', found, 'oldStr length:', oldStr.length, 'content length:', content.length)
        if (!found) {
          console.warn('[applyCanvasEdit] WARNING: old_str not found in documentContent')
          console.log('[applyCanvasEdit] old_str (first 100):', oldStr.slice(0, 100))
          console.log('[applyCanvasEdit] content (first 200):', content.slice(0, 200))
        }
        const newContent = content.replace(oldStr, newStr)
        return { documentContent: newContent }
      }),

      applyCanvasInsert: (insertLine: number, insertText: string) => set((state) => {
        const content = state.documentContent || ''
        const lines = content.split('\n')
        lines.splice(insertLine, 0, insertText)
        return { documentContent: lines.join('\n') }
      }),
    }),
    {
      name: 'content-d-store',
      partialize: (state) => ({
        // Persist session state
        sessionId: state.sessionId,
        sessionName: state.sessionName,
        selectedContentFoolscapId: state.selectedContentFoolscapId,
        contentFoolscapSnapshot: state.contentFoolscapSnapshot,
        contentDMode: state.contentDMode,
        hasInitialDraftRun: state.hasInitialDraftRun,
        documentContent: state.documentContent,
        documentCleanContent: state.documentCleanContent,
        voiceDirection: state.voiceDirection,
        gatekeeperTurns: state.gatekeeperTurns,
        zinsserDoc: state.zinsserDoc,
        zinsserDiagnosis: state.zinsserDiagnosis,
        // Don't persist streaming state, processing flags, or transient UI state
      }),
    }
  )
)
